import React from "react";
import {
  Box,
  useMediaQuery,
  useTheme,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import {
  MenuBuilderProvider,
  useMenuBuilder,
} from "./context/MenuBuilderContext";
import { IFoodMenuBuilderProps } from "./types/menuBuilder.types";
import { CategoryPanel } from "./components/CategoryPanel";
import { ItemGrid } from "./components/ItemGrid";
import { OrderSummaryPanel } from "./components/OrderSummaryPanel";

// ─── Inner Content Component ────────────────────────────────────────────────────

const FoodMenuBuilderContent: React.FC<{
  mode: "master" | "lead";
  onSave: (payload: any) => void;
  onPreview?: (previewData: any) => void;
  leadUuid?: string;
  masterMenuUuid?: string;
  masterMenuName?: string;
  masterMenuPrice?: string | null;
  saving?: boolean;
  tags?: string[];
  imageUrl?: string | null;
  saveButtonLabel?: string;
}> = ({ mode, onSave, onPreview, leadUuid, masterMenuUuid, masterMenuName, masterMenuPrice, saving, tags, imageUrl, saveButtonLabel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { categoriesLoading, categoriesError, activeTab, setActiveTab, retryLoadCategories, activeCategory } =
    useMenuBuilder();

  // ─── Error State ────────────────────────────────────────────────────────────

  if (categoriesError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
      >
        <Typography variant="h6" color="error" gutterBottom>
          {categoriesError}
        </Typography>
        <Button variant="contained" onClick={retryLoadCategories}>
          Retry
        </Button>
      </Box>
    );
  }

  // ─── Loading State ──────────────────────────────────────────────────────────

  if (categoriesLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading categories...
        </Typography>
      </Box>
    );
  }

  // ─── Mobile Layout ──────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          position: "relative",
        }}
      >
        {/* Content area */}
        <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
          {activeTab === "categories" && (
            <Box>
              <CategoryPanel />
            </Box>
          )}
          {activeTab === "items" && <ItemGrid />}
          {activeTab === "summary" && (
            <OrderSummaryPanel
              mode={mode}
              onSave={onSave}
              leadUuid={leadUuid}
              masterMenuUuid={masterMenuUuid}
              masterMenuName={masterMenuName}
              masterMenuPrice={masterMenuPrice}
              saving={saving}
              tags={tags}
              imageUrl={imageUrl}
              saveButtonLabel={saveButtonLabel}
            />
          )}
        </Box>

        {/* Bottom Navigation */}
        <Paper
          sx={{ position: "sticky", bottom: 0, left: 0, right: 0 }}
          elevation={3}
        >
          <BottomNavigation
            value={activeTab}
            onChange={(_event, newValue) => setActiveTab(newValue)}
            showLabels
          >
            <BottomNavigationAction
              label="Categories"
              value="categories"
              icon={<FolderOpenIcon />}
            />
            <BottomNavigationAction
              label="Items"
              value="items"
              icon={<RestaurantMenuIcon />}
            />
            <BottomNavigationAction
              label="Summary"
              value="summary"
              icon={<ReceiptLongIcon />}
            />
          </BottomNavigation>
        </Paper>
      </Box>
    );
  }

  // ─── Desktop Layout ─────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        height: "85vh",
        display: "flex",
        overflow: "hidden",
        borderRadius: "16px",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}
    >
      {/* Left Column: Category Panel (dark sidebar) */}
      <Box
        sx={{
          flex: "0 0 260px",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CategoryPanel />
      </Box>

      {/* Center Column: Item Grid - only shown when a category is selected */}
      <Box
        sx={{
          flex: activeCategory ? 1 : "0 0 0px",
          height: "100%",
          overflow: activeCategory ? "auto" : "hidden",
          opacity: activeCategory ? 1 : 0,
          transition: "flex 0.3s ease, opacity 0.3s ease",
          bgcolor: "#FFFFFF",
        }}
      >
        {activeCategory && <ItemGrid />}
      </Box>

      {/* Right Column: Order Summary Panel - takes remaining space when no category selected */}
      <Box
        sx={{
          flex: activeCategory ? "0 0 350px" : 1,
          height: "100%",
          overflow: "hidden",
          transition: "flex 0.3s ease",
        }}
      >
        <OrderSummaryPanel
          mode={mode}
          onSave={onSave}
          onPreview={onPreview}
          leadUuid={leadUuid}
          masterMenuUuid={masterMenuUuid}
          masterMenuName={masterMenuName}
          masterMenuPrice={masterMenuPrice}
          saving={saving}
          expanded={!activeCategory}
          tags={tags}
          imageUrl={imageUrl}
          saveButtonLabel={saveButtonLabel}
        />
      </Box>
    </Box>
  );
};

// ─── Root FoodMenuBuilder Component ─────────────────────────────────────────────

export const FoodMenuBuilder: React.FC<IFoodMenuBuilderProps> = ({
  mode = "lead",
  initialMenuData,
  initialLimits,
  initialPrice,
  initialIsFixed,
  masterMenuUuid,
  masterMenuName,
  masterMenuPrice,
  title,
  onSave,
  onPreview,
  leadUuid,
  saving,
  initialTags,
  initialImageUrl,
  canEditLimits,
  canEditVisibility,
  saveButtonLabel,
}) => {
  // Derive mode from flags if not explicitly passed
  const resolvedMode = canEditLimits || canEditVisibility ? "master" : mode;

  return (
    <MenuBuilderProvider
      mode={resolvedMode}
      initialMenuData={initialMenuData}
      initialLimits={initialLimits}
      initialPrice={initialPrice}
      initialIsFixed={initialIsFixed}
      masterMenuUuid={masterMenuUuid}
      title={title}
    >
      <FoodMenuBuilderContent
        mode={resolvedMode}
        onSave={onSave as any}
        onPreview={onPreview}
        leadUuid={leadUuid}
        masterMenuUuid={masterMenuUuid}
        masterMenuName={masterMenuName}
        masterMenuPrice={masterMenuPrice}
        saving={saving}
        tags={initialTags}
        imageUrl={initialImageUrl}
        saveButtonLabel={saveButtonLabel}
      />
    </MenuBuilderProvider>
  );
};
